import { UnstyledLink } from '@ardoq/link';
import { ButtonType, CopyToClipboard, IconButton } from '@ardoq/button';
import { colors } from '@ardoq/design-tokens';
import {
  ButtonWithDropdown,
  simpleNotifier,
  type DropdownOption,
} from '@ardoq/dropdown-menu';
import { Icon, IconName, MinimalIconProps } from '@ardoq/icons';
import { FlexBox } from '@ardoq/layout';
import { Tag, TagProps } from '@ardoq/status-ui';
import { Text } from '@ardoq/typography';
import { PlainTextPopover, WithUncontrolledPopover } from '@ardoq/popovers';
import React, { ReactNode, useRef } from 'react';
import styled from 'styled-components';
import {
  ActionsCellLabels,
  DEFAULT_ACTIONS_CELL_LABELS,
  DEFAULT_NAME_CELL_LABELS,
  DEFAULT_TRANSFORMATION_CELL_LABELS,
  NameCellLabels,
  TransformationCellLabels,
} from './labels';

const StyledCopyToClipboard = styled(CopyToClipboard)`
  opacity: 0;

  .ag-row:hover & {
    opacity: 1;
  }
`;

export type NameCellProps = {
  value: string;
  labels?: Partial<NameCellLabels>;
};

export const NameCell = (props: NameCellProps) => {
  const labels = { ...DEFAULT_NAME_CELL_LABELS, ...props.labels };
  return (
    <FlexBox align="center" gap="small">
      <Text truncate>{props.value}</Text>
      <StyledCopyToClipboard
        copyText={props.value}
        tooltipText={labels.copyName}
      />
    </FlexBox>
  );
};

export type FieldTypeCellProps = {
  icon: IconName;
  label: string;
};

export const FieldTypeCell = (props: FieldTypeCellProps) => (
  <FlexBox gap="xsmall" align="center">
    <Icon iconName={props.icon} color={colors.icon} />
    {props.label}
  </FlexBox>
);

export type TransformationCellProps = {
  label: string;
  icon: IconName;
  iconStatus?: null | {
    isError: boolean;
    popover: ReactNode;
  };
  recalculation: null | {
    onClick: VoidFunction;
    status: 'idle' | 'recalculating';
  };
  labels?: Partial<TransformationCellLabels>;
};

export const TransformationCell = (props: TransformationCellProps) => {
  const labels = { ...DEFAULT_TRANSFORMATION_CELL_LABELS, ...props.labels };
  const icon = (
    <Icon
      iconName={props.icon}
      color={props.iconStatus?.isError ? colors.iconError : colors.icon}
    />
  );
  return (
    <FlexBox gap="small" align="center">
      {props.iconStatus ? (
        <WithUncontrolledPopover
          content={
            <PlainTextPopover>{props.iconStatus.popover}</PlainTextPopover>
          }
        >
          {icon}
        </WithUncontrolledPopover>
      ) : (
        icon
      )}
      {props.label}
      {props.recalculation && (
        <AGGridTableButtonWrapper>
          <IconButton
            iconName={
              props.recalculation.status === 'idle'
                ? IconName.REFRESH
                : IconName.NONE
            }
            isLoading={props.recalculation.status === 'recalculating'}
            isDisabled={props.recalculation.status === 'recalculating'}
            label={labels.triggerRecalculation}
            onClick={props.recalculation.onClick}
          />
        </AGGridTableButtonWrapper>
      )}
    </FlexBox>
  );
};

export type TagCellProps = TagProps & {
  justify?: 'center' | 'start' | 'end';
};

export const TagCell = ({ justify = 'center', ...props }: TagCellProps) => (
  <FlexBox align="center" justify={justify} width="full" height="full">
    <Tag {...props} />
  </FlexBox>
);

/**
 * Prevents event propagation for child component inside AG grid cell. Allowing a button
 * to be clicked without triggering row selection.
 *
 * AG grid requires propagation to be stopped in the capture phase
 * @see https://github.com/ag-grid/ag-grid/issues/8528#issuecomment-2285984117
 */
export const AGGridTableButtonWrapper = ({
  children,
}: React.PropsWithChildren) => {
  const child = React.Children.only(children);

  return (
    <div
      style={{ display: 'contents' }}
      onClickCapture={evt => {
        evt.stopPropagation();

        // Trigger the child's onClick if it exists
        if (
          React.isValidElement<{ onClick?: (event: React.MouseEvent) => void }>(
            child
          ) &&
          child.props.onClick
        ) {
          child.props.onClick(evt);
        }
      }}
    >
      {children}
    </div>
  );
};

export type LinkNameCellProps = {
  displayName: string;
  href?: string;
  onClick: VoidFunction;
};

export const LinkNameCell = (props: LinkNameCellProps) => (
  <UnstyledLink
    onClick={props.onClick}
    href={props.href}
    style={{ textDecoration: 'underline' }}
  >
    <Text truncate>{props.displayName}</Text>
  </UnstyledLink>
);

type ActionsCellItem = Pick<
  DropdownOption,
  'label' | 'onClick' | 'buttonType' | 'isDisabled' | 'tooltipText'
> & {
  icon?: MinimalIconProps;
};

export type ActionsCellProps = {
  items: ActionsCellItem[];
  labels?: Partial<ActionsCellLabels>;
};

const toDropdownOption = ({
  icon,
  ...item
}: ActionsCellItem): Pick<
  DropdownOption,
  'label' | 'onClick' | 'leftIcon' | 'buttonType' | 'isDisabled' | 'tooltipText'
> => ({
  ...item,
  leftIcon: icon,
});

export const ActionsCell = (props: ActionsCellProps) => {
  const labels = { ...DEFAULT_ACTIONS_CELL_LABELS, ...props.labels };
  const requestOpen = useRef(simpleNotifier()).current;
  const openInCapturePhase = (event: React.MouseEvent) => {
    // AG Grid's row-click listener only yields when propagation is stopped in
    // the capture phase, which also suppresses the button's own click-to-open,
    // so the dropdown is opened imperatively via requestOpen instead.
    // https://github.com/ag-grid/ag-grid/issues/8528#issuecomment-2285984117
    event.stopPropagation();
    if (props.items.length) {
      requestOpen.notify();
    }
  };
  return (
    <FlexBox height="full" align="center" justify="flex-start">
      <ButtonWithDropdown
        options={props.items.map(toDropdownOption)}
        hideChevron
        buttonType={ButtonType.GHOST}
        aria-label={labels.actions}
        onClickCapture={openInCapturePhase}
        requestOpen={requestOpen}
      >
        <Icon iconName={IconName.MORE_VERT} />
      </ButtonWithDropdown>
    </FlexBox>
  );
};
